import {
  Alert,
  Button,
  Group,
  Loader,
  NativeSelect,
  PasswordInput,
  Stack,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";

import { useOrgScopedQueryKey } from "../../query/scope";
import {
  ConnectConfirmModal,
  DisconnectConfirmModal,
  PrincipalNotice,
  TrackerLiveStatus,
  TrackerSummary,
  TrackerWarnings,
  type LoginChange,
  type PendingConnect,
} from "./TaskTrackerParts";
import {
  deleteTrackerConnection,
  putTrackerConnection,
  verifyTracker,
  type TrackerConnection,
  type TrackerConnectionBody,
  type TrackerVerifyResult,
} from "./taskTrackerApi";
import { describeTrackerError, normalizeHostUrl } from "./trackerErrors";
import { useForgejoStatus } from "./useForgejoStatus";
import {
  TRACKER_CONNECTION_KEY,
  TRACKER_STATUS_KEY,
  useTrackerConnection,
  useTrackerProviders,
  useTrackerStatus,
} from "./useTaskTracker";

// Секция «Трекер задач» на `/settings/connections` (ADR-0075).
//
// Отдельное от Forgejo-секции подключение: даже на том же сервере
// токен трекера вводится отдельно от токена репо-хоста. Форма всегда
// видна — и до, и после подключения, чтобы можно было переподключить
// другой провайдер/хост/токен без явного режима редактирования.
//
// Два похода в сеть не пишут: «Проверить» (`verifyTracker`) и
// «Проверить доступность» (`GET /status`). Запись — только после
// подтверждения в модалке «Подключить».

/**
 * Предупреждение о смене учётной записи: только когда подключение уже
 * есть, сохранённый логин известен и новый логин от него отличается.
 * Модульная функция (не замыкание компонента) — не зависит от порядка
 * объявления хуков/мутаций в `TaskTrackerSection`.
 */
function computeLoginChange(
  connectionData: TrackerConnection | undefined,
  newLogin: string,
): LoginChange | undefined {
  if (!connectionData?.connected) {
    return undefined;
  }
  const stored = connectionData.principal_login;
  if (stored === null || stored === newLogin) {
    return undefined;
  }
  return { previous: stored, next: newLogin };
}

/**
 * Предупреждение о смене адреса трекера/провайдера: только когда
 * подключение уже есть и провайдер или нормализованный хост отличаются
 * от сохранённых.
 */
function computeTargetChange(
  connectionData: TrackerConnection | undefined,
  provider: string,
  hostUrl: string,
): boolean {
  if (!connectionData?.connected) {
    return false;
  }
  const providerChanged = provider !== connectionData.provider;
  const hostChanged =
    normalizeHostUrl(hostUrl) !== (connectionData.host_url ?? "");
  return providerChanged || hostChanged;
}

export function TaskTrackerSection(): ReactElement {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const providers = useTrackerProviders();
  const connection = useTrackerConnection();
  const forgejoStatus = useForgejoStatus();

  const isConnected = connection.data?.connected === true;
  const status = useTrackerStatus(isConnected);

  // Полный ключ кэша подключения ТЕКУЩЕЙ организации — тем же
  // способом построения, что у `useTrackerConnection`
  // (`src/query/scope.ts`). `!` безопасен: кнопки, вызывающие мутации
  // ниже, рисуются только после успешной загрузки `connection`, а он
  // сам org-scoped — без активной организации он не загрузился бы.
  const connectionQueryKey = useOrgScopedQueryKey(TRACKER_CONNECTION_KEY)!;

  // `null` — черновик ещё не правили в этом рендере формы; тогда поле
  // показывает сохранённое значение (образец — `pathDraft` в
  // `CoderSystemSection`).
  const [providerDraft, setProviderDraft] = useState<string | null>(null);
  const [hostDraft, setHostDraft] = useState<string | null>(null);
  const [pat, setPat] = useState("");
  const [urlError, setUrlError] = useState<string | null>(null);
  const [patError, setPatError] = useState<string | null>(null);
  const [verifyResult, setVerifyResult] = useState<TrackerVerifyResult | null>(
    null,
  );
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [pendingConnect, setPendingConnect] = useState<PendingConnect | null>(
    null,
  );
  const [disconnectOpen, setDisconnectOpen] = useState(false);

  const verifyMutation = useMutation<
    TrackerVerifyResult,
    Error,
    TrackerConnectionBody
  >({
    mutationFn: (body) => verifyTracker(body),
    onSuccess: (data) => {
      setVerifyResult(data);
      setSubmitError(null);
    },
    onError: (err) => {
      setVerifyResult(null);
      setSubmitError(describeTrackerError(err, t));
    },
  });

  const connectVerifyMutation = useMutation<
    TrackerVerifyResult,
    Error,
    TrackerConnectionBody
  >({
    mutationFn: (body) => verifyTracker(body),
    onSuccess: (data, variables) => {
      setSubmitError(null);
      setPendingConnect({
        principalLogin: data.principal_login,
        loginChange: computeLoginChange(connection.data, data.principal_login),
        targetChange: computeTargetChange(
          connection.data,
          variables.provider,
          variables.host_url,
        ),
      });
    },
    onError: (err) => {
      setPendingConnect(null);
      setSubmitError(describeTrackerError(err, t));
    },
  });

  // Ключ кэша фиксируется в `onMutate` и приходит в `onSuccess`
  // контекстом: ответ пишется в кэш той организации, в которой запрос
  // отправлен, даже если организацию сменили, пока запрос был в полёте.
  const putMutation = useMutation<
    TrackerConnection,
    Error,
    TrackerConnectionBody,
    readonly unknown[]
  >({
    mutationFn: (body) => putTrackerConnection(body),
    onMutate: () => connectionQueryKey,
    onSuccess: (data, _variables, key) => {
      queryClient.setQueryData(key, data);
      void queryClient.resetQueries({ queryKey: TRACKER_STATUS_KEY });
      setPat("");
      setProviderDraft(null);
      setHostDraft(null);
      setPendingConnect(null);
      setSubmitError(null);
      setVerifyResult(null);
      setSuccessMessage(
        t("connections.taskTracker.connectSuccess", {
          login: data.principal_login ?? "",
        }),
      );
    },
    onError: (err) => {
      setPendingConnect(null);
      setSubmitError(describeTrackerError(err, t));
    },
  });

  const deleteMutation = useMutation<void, Error, void, readonly unknown[]>({
    mutationFn: () => deleteTrackerConnection(),
    onMutate: () => connectionQueryKey,
    onSuccess: (_data, _variables, key) => {
      const cleared: TrackerConnection = {
        connected: false,
        provider: null,
        host_url: null,
        has_credentials: false,
        principal_login: null,
      };
      queryClient.setQueryData(key, cleared);
      queryClient.removeQueries({ queryKey: TRACKER_STATUS_KEY });
      setDisconnectOpen(false);
      setSubmitError(null);
      setVerifyResult(null);
      setProviderDraft(null);
      setHostDraft(null);
      setPat("");
      setSuccessMessage(t("connections.taskTracker.disconnect.success"));
    },
    onError: (err) => {
      setDisconnectOpen(false);
      setSubmitError(describeTrackerError(err, t));
    },
  });

  if (providers.isPending || connection.isPending) {
    return <Loader />;
  }

  if (providers.isError || connection.isError) {
    return (
      <Stack gap="md">
        <Title order={2}>{t("connections.taskTracker.title")}</Title>
        <Alert color="red" title={t("connections.errorHeading")}>
          {t("connections.loadingError")}
        </Alert>
      </Stack>
    );
  }

  const providerList = providers.data;
  const connectionData = connection.data;

  // Сохранённый провайдер может не входить в актуальный каталог
  // (`/providers` вернул только часть провайдеров, которые
  // поддерживает эта установка) — тогда дефолт показывает первый
  // доступный, а не недоступное значение.
  const storedProviderAvailable = providerList.some(
    (p) => p.name === connectionData.provider,
  );
  // Если `storedProviderAvailable`, `connectionData.provider` совпал с
  // `name` записи каталога, а `name` — строка: значение не `null`.
  const defaultProvider = storedProviderAvailable
    ? connectionData.provider!
    : (providerList[0]?.name ?? "");
  const providerValue = providerDraft ?? defaultProvider;
  const hostValue = hostDraft ?? connectionData.host_url ?? "";

  const repoHostUrl = forgejoStatus.data?.host_url ?? null;
  const showHostHint =
    forgejoStatus.data?.connected === true &&
    repoHostUrl !== null &&
    hostValue.trim() === "";

  const validate = (): boolean => {
    const hostEmpty = hostValue.trim() === "";
    const patEmpty = pat.trim() === "";
    setUrlError(hostEmpty ? t("connections.taskTracker.hostRequired") : null);
    setPatError(patEmpty ? t("connections.taskTracker.patRequired") : null);
    return !hostEmpty && !patEmpty;
  };

  const buildBody = (): TrackerConnectionBody => ({
    provider: providerValue,
    host_url: hostValue,
    credentials: { pat },
  });

  const handleVerifyClick = (): void => {
    setSubmitError(null);
    setVerifyResult(null);
    setSuccessMessage(null);
    if (!validate()) {
      return;
    }
    verifyMutation.mutate(buildBody());
  };

  const handleConnectClick = (): void => {
    setSubmitError(null);
    setVerifyResult(null);
    setSuccessMessage(null);
    if (!validate()) {
      return;
    }
    connectVerifyMutation.mutate(buildBody());
  };

  const handleCancelConnect = (): void => {
    setPendingConnect(null);
  };

  const handleConfirmConnect = (): void => {
    putMutation.mutate(buildBody());
  };

  const handleDisconnectClick = (): void => {
    setSubmitError(null);
    setSuccessMessage(null);
    setDisconnectOpen(true);
  };

  const handleCancelDisconnect = (): void => {
    setDisconnectOpen(false);
  };

  const handleConfirmDisconnect = (): void => {
    deleteMutation.mutate();
  };

  const handleRecheckStatus = (): void => {
    void queryClient.invalidateQueries({ queryKey: TRACKER_STATUS_KEY });
  };

  return (
    <Stack gap="md">
      <Group justify="space-between" align="center">
        <Title order={2}>{t("connections.taskTracker.title")}</Title>
        {isConnected ? (
          <Button
            color="red"
            variant="subtle"
            onClick={handleDisconnectClick}
            loading={deleteMutation.isPending}
          >
            {t("connections.taskTracker.disconnectButton")}
          </Button>
        ) : null}
      </Group>

      <Text c="dimmed">{t("connections.taskTracker.intro")}</Text>

      <TrackerSummary connection={connectionData} />

      {isConnected ? (
        <TrackerLiveStatus
          query={status}
          storedLogin={connectionData.principal_login}
          onRecheck={handleRecheckStatus}
        />
      ) : null}

      <Stack gap="sm">
        <NativeSelect
          label={t("connections.taskTracker.providerLabel")}
          data={providerList.map((p) => p.name)}
          value={providerValue}
          onChange={(event) => {
            setProviderDraft(event.currentTarget.value);
            setVerifyResult(null);
          }}
        />
        <TextInput
          label={t("connections.taskTracker.hostLabel")}
          placeholder={t("connections.taskTracker.hostPlaceholder")}
          value={hostValue}
          onChange={(event) => {
            setHostDraft(event.currentTarget.value);
            setUrlError(null);
            setVerifyResult(null);
          }}
          error={urlError}
        />
        {showHostHint && repoHostUrl !== null ? (
          <Button
            variant="subtle"
            size="xs"
            onClick={() => {
              setHostDraft(repoHostUrl);
              setVerifyResult(null);
            }}
          >
            {t("connections.taskTracker.useRepoHostUrl", {
              host: repoHostUrl,
            })}
          </Button>
        ) : null}
        <PasswordInput
          label={t("connections.taskTracker.patLabel")}
          description={t("connections.taskTracker.patHint")}
          value={pat}
          onChange={(event) => {
            setPat(event.currentTarget.value);
            setPatError(null);
            setVerifyResult(null);
          }}
          error={patError}
        />
      </Stack>

      {verifyResult === null ? null : (
        <Stack gap="sm">
          <PrincipalNotice login={verifyResult.principal_login} />
          <TrackerWarnings
            loginChange={computeLoginChange(
              connectionData,
              verifyResult.principal_login,
            )}
            targetChange={computeTargetChange(
              connectionData,
              providerValue,
              hostValue,
            )}
          />
        </Stack>
      )}

      {successMessage === null ? null : (
        <Alert color="blue">{successMessage}</Alert>
      )}

      {submitError === null ? null : (
        <Alert color="red" title={t("connections.errorHeading")}>
          {submitError}
        </Alert>
      )}

      <Group>
        <Button
          variant="default"
          onClick={handleVerifyClick}
          loading={verifyMutation.isPending}
        >
          {t("connections.taskTracker.verifyButton")}
        </Button>
        <Button
          onClick={handleConnectClick}
          loading={connectVerifyMutation.isPending || putMutation.isPending}
        >
          {t("connections.taskTracker.connectButton")}
        </Button>
      </Group>

      <ConnectConfirmModal
        pending={pendingConnect}
        isSubmitting={putMutation.isPending}
        onConfirm={handleConfirmConnect}
        onCancel={handleCancelConnect}
      />
      <DisconnectConfirmModal
        opened={disconnectOpen}
        isSubmitting={deleteMutation.isPending}
        onConfirm={handleConfirmDisconnect}
        onCancel={handleCancelDisconnect}
      />
    </Stack>
  );
}
